import React from 'react';
import {
    Building2, Plus, Loader, X, XCircle, CheckCircle,
    RefreshCw, Settings, PowerOff, Save,
    ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import MultiSelect from '../ui/MultiSelect';

const DAY_OPTIONS = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' },
];

const formatHour = (h) => {
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:00 ${ampm} UTC`;
};

export default function EmployersTab({
    employers,
    employersLoading,
    allEmployers,
    employerError,
    setEmployerError,
    rescrapingEmployerId,
    industries,
    employerStatusFilter,
    setEmployerStatusFilter,
    // Pagination (server-side, 20/page)
    page,
    setPage,
    totalPages,
    total,
    sortBy,
    sortDir,
    onSortChange,
    // Add employer modal
    showAddEmployerModal,
    setShowAddEmployerModal,
    newEmployer,
    setNewEmployer,
    addingEmployer,
    onAddEmployer,
    // Edit employer modal
    showEmployerEditModal,
    setShowEmployerEditModal,
    editingEmployer,
    setEditingEmployer,
    savingEmployer,
    onSaveEmployer,
    onEditEmployer,
    // Actions
    onDeleteEmployer,
    onRescrapeEmployer,
}) {
    const { isDarkMode } = useTheme();

    const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : 'border-slate-300 text-black'}`;
    const labelClass = `block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`;

    // Derive unique locations from every employer (not just the current page) for datalist suggestions
    const existingLocations = [...new Set((allEmployers || []).map(e => e.Location).filter(Boolean))].sort();

    // Primary industry first, then any additional tags (deduped against the primary)
    const industryTags = (emp) => [
        ...(emp.IndustryName ? [emp.IndustryName] : []),
        ...((emp.Industries || []).filter(n => n !== emp.IndustryName)),
    ];

    function SortIcon({ field }) {
        if (sortBy !== field) return <ChevronsUpDown size={13} className="opacity-40 ml-1 inline-block" />;
        return sortDir === 'asc'
            ? <ChevronUp size={13} className="ml-1 inline-block" />
            : <ChevronDown size={13} className="ml-1 inline-block" />;
    }

    const thSortClass = `px-6 py-3 text-left text-xs font-medium uppercase cursor-pointer select-none hover:opacity-80 transition-opacity ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;

    return (
        <div className="space-y-6">
            {/* Error Message */}
            {employerError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                        <h3 className="font-medium text-red-900">Employer Error</h3>
                        <p className="text-sm text-red-700 mt-1">{employerError}</p>
                    </div>
                    <button
                        onClick={() => setEmployerError(null)}
                        className="ml-auto p-1 hover:bg-red-100 rounded"
                    >
                        <X size={16} className="text-red-600" />
                    </button>
                </div>
            )}

            {/* Header */}
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Employers</h2>
                    {/* Active / Inactive / All filter */}
                    <div className={`flex rounded-lg border overflow-hidden text-sm ${isDarkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                        {['active', 'inactive', 'all'].map(f => (
                            <button
                                key={f}
                                onClick={() => setEmployerStatusFilter(f)}
                                className={`px-3 py-1 capitalize transition-colors ${
                                    employerStatusFilter === f
                                        ? 'bg-primary-600 text-white'
                                        : isDarkMode
                                            ? 'text-slate-400 hover:text-slate-100 hover:bg-slate-700'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                }`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>
                <button
                    onClick={() => setShowAddEmployerModal(true)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                >
                    <Plus size={18} />
                    Add Employer
                </button>
            </div>

            {/* Employers List */}
            {employersLoading ? (
                <div className={`rounded-2xl border shadow-sm p-12 text-center ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <Loader className="animate-spin mx-auto text-primary-600 mb-4" size={48} />
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Loading employers...</p>
                </div>
            ) : (
                <div className={`rounded-2xl border shadow-sm overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className={`border-b ${isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                                <tr>
                                    <th className={thSortClass} onClick={() => onSortChange('Name')}>Name<SortIcon field="Name" /></th>
                                    <th className={thSortClass} onClick={() => onSortChange('IndustryName')}>Industry<SortIcon field="IndustryName" /></th>
                                    <th className={thSortClass} onClick={() => onSortChange('Location')}>Location<SortIcon field="Location" /></th>
                                    <th className={thSortClass} onClick={() => onSortChange('JobCount')}>Job Count<SortIcon field="JobCount" /></th>
                                    <th className={thSortClass} onClick={() => onSortChange('Portal')}>Portal<SortIcon field="Portal" /></th>
                                    <th className={thSortClass} onClick={() => onSortChange('RetirementThreshold')}>Retire After<SortIcon field="RetirementThreshold" /></th>
                                    {employerStatusFilter !== 'active' && (
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Status</th>
                                    )}
                                    <th className={`px-6 py-3 text-right text-xs font-medium uppercase ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
                                {employers.map(employer => (
                                    <tr key={employer.EmployerID} className={isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-50'}>
                                        <td className={`px-6 py-4 font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{employer.Name}</td>
                                        <td className={`px-6 py-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                            {industryTags(employer).length === 0 ? 'N/A' : (
                                                <div className="flex flex-wrap gap-1">
                                                    {industryTags(employer).map((t, i) => (
                                                        <span key={t} className={`px-2 py-0.5 rounded text-xs ${i === 0
                                                            ? 'bg-primary-100 text-primary-700'
                                                            : (isDarkMode ? 'bg-slate-600 text-slate-200' : 'bg-slate-100 text-slate-600')}`}>{t}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        <td className={`px-6 py-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{employer.Location || 'N/A'}</td>
                                        <td className="px-6 py-4">
                                            <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-sm">
                                                {employer.JobCount || 0}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            {employer.WorkdayURL ? (
                                                <CheckCircle className="text-primary-600" size={18} />
                                            ) : (
                                                <XCircle className="text-slate-400" size={18} />
                                            )}
                                        </td>
                                        <td className={`px-6 py-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                            {employer.RetirementThreshold ?? 60}d
                                        </td>
                                        {employerStatusFilter !== 'active' && (
                                            <td className="px-6 py-4">
                                                {employer.isActive ? (
                                                    <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">Active</span>
                                                ) : (
                                                    <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">Inactive</span>
                                                )}
                                            </td>
                                        )}
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                {employer.WorkdayURL && (
                                                    <button
                                                        onClick={() => onRescrapeEmployer(employer)}
                                                        disabled={rescrapingEmployerId === employer.EmployerID}
                                                        className="p-2 hover:bg-primary-100 text-primary-600 rounded-lg transition-colors disabled:opacity-50"
                                                        title="Reload jobs"
                                                    >
                                                        {rescrapingEmployerId === employer.EmployerID ? (
                                                            <Loader className="animate-spin" size={16} />
                                                        ) : (
                                                            <RefreshCw size={16} />
                                                        )}
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => onEditEmployer(employer)}
                                                    className="p-2 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                                                    title="Edit employer"
                                                >
                                                    <Settings size={16} />
                                                </button>
                                                <button
                                                    onClick={() => onDeleteEmployer(employer.EmployerID, employer.Name)}
                                                    className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                                                    title="Deactivate employer"
                                                >
                                                    <PowerOff size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className={`flex items-center justify-between px-6 py-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Showing {employers.length} of {total} employers
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage(Math.max(1, page - 1))}
                                    disabled={page === 1}
                                    className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                        isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'
                                    }`}
                                >
                                    <ChevronLeft size={18} className={isDarkMode ? 'text-slate-300' : 'text-slate-700'} />
                                </button>
                                <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Page {page} of {totalPages}</span>
                                <button
                                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                                    disabled={page === totalPages}
                                    className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                        isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'
                                    }`}
                                >
                                    <ChevronRight size={18} className={isDarkMode ? 'text-slate-300' : 'text-slate-700'} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Edit Employer Modal */}
            {showEmployerEditModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-16 overflow-y-auto">
                    <div className={`rounded-2xl shadow-2xl max-w-2xl w-full ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                        <div className={`p-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <div className="flex items-center justify-between">
                                <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Edit Employer</h2>
                                <button
                                    onClick={() => {
                                        setShowEmployerEditModal(false);
                                        setEmployerError(null);
                                    }}
                                    className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100'}`}
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {employerError && (
                            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                                <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
                                <p className="text-sm text-red-700">{employerError}</p>
                            </div>
                        )}

                        <div className="p-6 space-y-4">
                            <div>
                                <label className={labelClass}>Company Name *</label>
                                <input
                                    type="text"
                                    value={editingEmployer?.Name || ''}
                                    onChange={(e) => setEditingEmployer(prev => ({ ...prev, Name: e.target.value }))}
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Location</label>
                                <input
                                    type="text"
                                    list="edit-locations-list"
                                    value={editingEmployer?.Location || ''}
                                    onChange={(e) => setEditingEmployer(prev => ({ ...prev, Location: e.target.value }))}
                                    className={inputClass}
                                    placeholder="City, State"
                                />
                                <datalist id="edit-locations-list">
                                    {existingLocations.map(loc => (
                                        <option key={loc} value={loc} />
                                    ))}
                                </datalist>
                            </div>

                            <div>
                                <label className={labelClass}>Primary Industry</label>
                                <select
                                    value={editingEmployer?.Industry || ''}
                                    onChange={(e) => setEditingEmployer(prev => ({ ...prev, Industry: e.target.value, IndustryName: e.target.value }))}
                                    className={inputClass}
                                >
                                    <option value="">— Select Industry —</option>
                                    {industries.map(ind => (
                                        <option key={ind.IndustryID} value={ind.IndustryName}>{ind.IndustryName}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className={labelClass}>Additional Industries</label>
                                <MultiSelect
                                    options={industries.map(i => i.IndustryName).filter(n => n !== (editingEmployer?.Industry || ''))}
                                    selected={editingEmployer?.Industries || []}
                                    onChange={(sel) => setEditingEmployer(prev => ({ ...prev, Industries: sel }))}
                                    placeholder="Add more industry tags..."
                                    searchable
                                    searchPlaceholder="Search industries..."
                                    isDarkMode={isDarkMode}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Career Site URL</label>
                                <input
                                    type="url"
                                    value={editingEmployer?.CareerSiteURL || ''}
                                    onChange={(e) => setEditingEmployer(prev => ({ ...prev, CareerSiteURL: e.target.value }))}
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Workday URL</label>
                                <input
                                    type="url"
                                    value={editingEmployer?.WorkdayURL || ''}
                                    onChange={(e) => setEditingEmployer(prev => ({ ...prev, WorkdayURL: e.target.value }))}
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Reload Interval</label>
                                <select
                                    value={editingEmployer?.RescrapeInterval || 'None'}
                                    onChange={(e) => setEditingEmployer(prev => ({
                                        ...prev,
                                        RescrapeInterval: e.target.value,
                                        RescrapeDay: prev.RescrapeDay ?? 3,
                                        RescrapeHour: prev.RescrapeHour ?? 20,
                                    }))}
                                    className={inputClass}
                                >
                                    <option value="None">None</option>
                                    <option value="Hourly">Hourly</option>
                                    <option value="Daily">Daily</option>
                                    <option value="Weekly">Weekly</option>
                                    <option value="Biweekly">Biweekly</option>
                                    <option value="Monthly">Monthly</option>
                                    <option value="Custom Weekly">Custom Weekly</option>
                                </select>

                                {editingEmployer?.RescrapeInterval === 'Custom Weekly' && (
                                    <div className="mt-3 space-y-3">
                                        <div>
                                            <label className={labelClass}>Day of Week</label>
                                            <select
                                                value={editingEmployer.RescrapeDay ?? 3}
                                                onChange={(e) => setEditingEmployer(prev => ({ ...prev, RescrapeDay: parseInt(e.target.value) }))}
                                                className={inputClass}
                                            >
                                                {DAY_OPTIONS.map(d => (
                                                    <option key={d.value} value={d.value}>{d.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Time (UTC)</label>
                                            <select
                                                value={editingEmployer.RescrapeHour ?? 20}
                                                onChange={(e) => setEditingEmployer(prev => ({ ...prev, RescrapeHour: parseInt(e.target.value) }))}
                                                className={inputClass}
                                            >
                                                {Array.from({ length: 24 }, (_, h) => (
                                                    <option key={h} value={h}>{formatHour(h)}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className={labelClass}>Retirement Threshold (days)</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="365"
                                    value={editingEmployer?.RetirementThreshold ?? 60}
                                    onChange={(e) => setEditingEmployer(prev => ({
                                        ...prev,
                                        RetirementThreshold: e.target.value === '' ? '' : parseInt(e.target.value)
                                    }))}
                                    className={inputClass}
                                    placeholder="60"
                                />
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Listings not seen after this many days will be retired. Default: 60 days.
                                </p>
                            </div>
                        </div>

                        <div className={`p-6 border-t flex items-center justify-end gap-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <button
                                onClick={() => setShowEmployerEditModal(false)}
                                className={`px-6 py-2 border rounded-lg transition-colors ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onSaveEmployer}
                                disabled={savingEmployer}
                                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                                {savingEmployer ? (
                                    <>
                                        <Loader className="animate-spin" size={18} />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Employer Modal */}
            {showAddEmployerModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-16 overflow-y-auto">
                    <div className={`rounded-2xl shadow-2xl max-w-2xl w-full ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                        <div className={`p-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <div className="flex items-center justify-between">
                                <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Add New Employer</h2>
                                <button
                                    onClick={() => {
                                        setShowAddEmployerModal(false);
                                        setEmployerError(null);
                                    }}
                                    className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100'}`}
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {employerError && (
                            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                                <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
                                <p className="text-sm text-red-700">{employerError}</p>
                            </div>
                        )}

                        <div className="p-6 space-y-4">
                            <div>
                                <label className={labelClass}>Company Name *</label>
                                <input
                                    type="text"
                                    value={newEmployer?.Name}
                                    onChange={(e) => setNewEmployer(prev => ({ ...prev, Name: e.target.value }))}
                                    className={inputClass}
                                    placeholder="Company name"
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Location</label>
                                <input
                                    type="text"
                                    list="add-locations-list"
                                    value={newEmployer?.Location}
                                    onChange={(e) => setNewEmployer(prev => ({ ...prev, Location: e.target.value }))}
                                    className={inputClass}
                                    placeholder="City, State"
                                />
                                <datalist id="add-locations-list">
                                    {existingLocations.map(loc => (
                                        <option key={loc} value={loc} />
                                    ))}
                                </datalist>
                            </div>

                            <div>
                                <label className={labelClass}>Primary Industry</label>
                                <select
                                    value={newEmployer.Industry}
                                    onChange={(e) => setNewEmployer(prev => ({ ...prev, Industry: e.target.value }))}
                                    className={inputClass}
                                >
                                    <option value="">— Select Industry —</option>
                                    {industries.map(ind => (
                                        <option key={ind.IndustryID} value={ind.IndustryName}>{ind.IndustryName}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className={labelClass}>Additional Industries</label>
                                <MultiSelect
                                    options={industries.map(i => i.IndustryName).filter(n => n !== newEmployer.Industry)}
                                    selected={newEmployer.Industries || []}
                                    onChange={(sel) => setNewEmployer(prev => ({ ...prev, Industries: sel }))}
                                    placeholder="Add more industry tags..."
                                    searchable
                                    searchPlaceholder="Search industries..."
                                    isDarkMode={isDarkMode}
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Career Site URL</label>
                                <input
                                    type="url"
                                    value={newEmployer.CareerSiteURL}
                                    onChange={(e) => setNewEmployer(prev => ({ ...prev, CareerSiteURL: e.target.value }))}
                                    className={inputClass}
                                    placeholder="https://"
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Workday Portal URL *</label>
                                <input
                                    type="url"
                                    value={newEmployer.WorkdayURL}
                                    onChange={(e) => setNewEmployer(prev => ({ ...prev, WorkdayURL: e.target.value }))}
                                    className={inputClass}
                                    placeholder="https://company.wd5.myworkdayjobs.com/..."
                                />
                            </div>

                            <div>
                                <label className={labelClass}>Retirement Threshold (days)</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="365"
                                    onChange={(e) => setNewEmployer(prev => ({
                                        ...prev,
                                        RetirementThreshold: e.target.value === '' ? '' : parseInt(e.target.value)
                                    }))}
                                    value={newEmployer.RetirementThreshold ?? 60}
                                    className={inputClass}
                                    placeholder="60"
                                />
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Listings not seen after this many days will be retired. Default: 60 days.
                                </p>
                            </div>
                        </div>

                        <div className={`p-6 border-t flex items-center justify-end gap-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <button
                                onClick={() => setShowAddEmployerModal(false)}
                                className={`px-6 py-2 border rounded-lg transition-colors ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onAddEmployer}
                                disabled={addingEmployer || !newEmployer.Name}
                                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                                {addingEmployer ? (
                                    <>
                                        <Loader className="animate-spin" size={18} />
                                        Adding...
                                    </>
                                ) : (
                                    <>
                                        <Plus size={18} />
                                        Add Employer
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}